/**
 * 2048 Game — Main Container Component
 *
 * Orchestrates: board size selection → game initialization → tile movement →
 * scoring → win/loss detection.
 *
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 * Keyboard arrow keys + touch swipe support.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { Game2048Engine } from '../Game2048Engine';
import { Board2048 } from './Board2048';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { Game2048ErrorHandler } from '../services/Game2048ErrorHandler';
import { Game2048StatisticsService } from '../services/Game2048StatisticsService';
import { Game2048GamePersistence } from '../services/Game2048GamePersistence';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import {
  Game2048Settings,
  Game2048State,
  Game2048Props,
  Direction,
  DEFAULT_BOARD_SIZE,
} from '../types';
import './Game2048.css';

type GameView = 'mode-select' | 'playing' | 'won' | 'game-over';

/** Minimum swipe distance in pixels */
const MIN_SWIPE_DISTANCE = 30;

const Game2048Inner: React.FC<Game2048Props> = ({ onExit }) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<Game2048Settings | null>(null);
  const [gameState, setGameState] = useState<Game2048State | null>(null);

  // Refs
  const engineRef = useRef<Game2048Engine>(new Game2048Engine());
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());
  const boardRef = useRef<HTMLDivElement>(null);
  const gameStateRef = useRef<Game2048State | null>(null);
  const settingsRef = useRef<Game2048Settings | null>(null);

  // Touch tracking
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  // Services (lazy init)
  const errorHandlerRef = useRef<Game2048ErrorHandler | null>(null);
  const statsServiceRef = useRef<Game2048StatisticsService | null>(null);
  const persistenceRef = useRef<Game2048GamePersistence | null>(null);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new Game2048ErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new Game2048StatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new Game2048GamePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize 2048 services:', e);
    }
  }, []);

  // =========================================================================
  // Game Flow
  // =========================================================================

  const handleStartGame = useCallback(
    (newSettings: Game2048Settings) => {
      const engine = engineRef.current;
      const boardSize = newSettings.boardSize || DEFAULT_BOARD_SIZE;
      const state = engine.initializeGame(boardSize);

      setSettings(newSettings);
      setGameState(state);
      setView('playing');
      gameStateRef.current = state;
      settingsRef.current = newSettings;

      // Track game session
      gameSessionIdRef.current = gameSessionManager.current.startGameSession('2048');

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Started',
        message: `${boardSize}×${boardSize} board ready. Swipe or use arrow keys!`,
        duration: 2000,
      });
    },
    [addNotification]
  );

  const handleMove = useCallback(
    (direction: Direction) => {
      const currentState = gameStateRef.current;
      if (!currentState) return;
      if (currentState.gameStatus === 'game-over') return;
      if (currentState.gameStatus === 'won' && !currentState.keepPlaying) return;

      const engine = engineRef.current;

      try {
        const newState = engine.move(direction);
        gameStateRef.current = newState;
        setGameState(newState);

        // Auto-save
        if (persistenceRef.current && settingsRef.current) {
          persistenceRef.current.autoSave(newState, {
            boardSize: newState.boardSize,
            startedAt: newState.startedAt,
            lastSavedAt: Date.now(),
          });
        }

        // Check win
        if (newState.gameStatus === 'won') {
          setView('won');
        }

        // Check game over
        if (newState.gameStatus === 'game-over') {
          handleGameOver(newState);
        }
      } catch (error) {
        // Silently ignore invalid moves
        console.warn('2048 move error:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleKeepPlaying = useCallback(() => {
    const engine = engineRef.current;
    try {
      const newState = engine.keepPlaying();
      setGameState(newState);
      setView('playing');

      addNotification({
        type: NotificationType.INFO,
        title: 'Keep Going!',
        message: 'See how high you can go!',
        duration: 2000,
      });
    } catch (error) {
      console.warn('Keep playing error:', error);
    }
  }, [addNotification]);

  const handleUndo = useCallback(() => {
    if (!gameStateRef.current) return;

    const engine = engineRef.current;
    const newState = engine.undo();
    gameStateRef.current = newState;
    setGameState(newState);

    // If we were in game-over, undo brings us back to playing
    if (newState.gameStatus !== 'game-over') {
      setView('playing');
    }
  }, []);

  const handleGameOver = useCallback(
    (state: Game2048State) => {
      setView('game-over');

      // End game session
      if (gameSessionIdRef.current) {
        gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
        gameSessionIdRef.current = null;
      }

      // Clear auto-save
      persistenceRef.current?.clearAutoSave();

      // Record statistics
      if (statsServiceRef.current) {
        const engine = engineRef.current;
        statsServiceRef.current.recordGameResult(
          {
            gameId: state.gameId,
            score: state.score,
            highestTile: engine.getHighestTile(),
            won: state.keepPlaying || engine.hasWon(),
            moveCount: state.moveCount,
            completedAt: Date.now(),
            boardSize: state.boardSize,
          },
          isAuthenticated ? user?.id : undefined
        );
      }

      addNotification({
        type: NotificationType.INFO,
        title: 'Game Over',
        message: `Final score: ${state.score}`,
        duration: 5000,
      });
    },
    [addNotification, isAuthenticated, user]
  );

  const handleNewGame = useCallback(() => {
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }

    // Re-initialize with same settings
    if (settings) {
      handleStartGame(settings);
    } else {
      setView('mode-select');
      setGameState(null);
      setSettings(null);
    }
  }, [settings, handleStartGame]);

  const handleExit = useCallback(() => {
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    onExit?.();
  }, [onExit]);

  // =========================================================================
  // Keyboard support
  // =========================================================================

  useEffect(() => {
    if (view !== 'playing' && !(view === 'won' || view === 'game-over')) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      let direction: Direction | null = null;

      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          direction = 'up';
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          direction = 'down';
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          direction = 'left';
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          direction = 'right';
          break;
      }

      if (direction) {
        e.preventDefault();
        handleMove(direction);
        return;
      }

      // Ctrl+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [view, handleMove, handleUndo]);

  // =========================================================================
  // Touch swipe support
  // =========================================================================

  useEffect(() => {
    const boardEl = boardRef.current;
    if (!boardEl) return;
    if (view !== 'playing') return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartRef.current) return;

      const touch = e.changedTouches[0];
      const dx = touch.clientX - touchStartRef.current.x;
      const dy = touch.clientY - touchStartRef.current.y;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      touchStartRef.current = null;

      if (Math.max(absDx, absDy) < MIN_SWIPE_DISTANCE) return;

      e.preventDefault();

      let direction: Direction;
      if (absDx > absDy) {
        direction = dx > 0 ? 'right' : 'left';
      } else {
        direction = dy > 0 ? 'down' : 'up';
      }

      handleMove(direction);
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Prevent page scrolling while swiping on the board
      if (touchStartRef.current) {
        e.preventDefault();
      }
    };

    boardEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    boardEl.addEventListener('touchmove', handleTouchMove, { passive: false });
    boardEl.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      boardEl.removeEventListener('touchstart', handleTouchStart);
      boardEl.removeEventListener('touchmove', handleTouchMove);
      boardEl.removeEventListener('touchend', handleTouchEnd);
    };
  }, [view, handleMove]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const game2048Config: GameModeConfig = {
      gameName: '2048',
      gameIcon: '🔢',
      availableModes: ['ai'],
      difficulties: [],
      extraSections: [{
        title: 'Board Size',
        icon: '📐',
        options: [
          { id: '4', label: '4×4', icon: '⬜' },
          { id: '5', label: '5×5', icon: '🔲' },
          { id: '6', label: '6×6', icon: '⬛' },
        ],
        defaultSelected: '4',
        defaultOpen: true,
        visibleForModes: ['ai'],
      }],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      const boardSize = parseInt(selection.extras?.['extra-0'] || '4', 10);
      handleStartGame({ boardSize });
    };

    return (
      <div className="game-2048">
        <div className="game-2048__header">
          <button
            className="game-2048__exit-btn"
            onClick={handleExit}
            aria-label="Back to Game Hub"
          >
            ← Game Hub
          </button>
        </div>
        <UniversalGameModeSelector
          config={game2048Config}
          onStart={handleModeSelection}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
          isAuthenticated={isAuthenticated}
        />
      </div>
    );
  }

  if (!gameState) return null;

  const gameOverConfig = view === 'game-over' ? {
    show: true,
    result: 'loss' as const,
    title: 'Game Over',
    subtitle: `Final score: ${gameState.score}`,
    icon: '🔢',
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : view === 'won' ? {
    show: true,
    result: 'win' as const,
    title: '🎉 You Win!',
    subtitle: `You reached 2048! Score: ${gameState.score}`,
    icon: '🎉',
    onPlayAgain: handleKeepPlaying,
    onExit: handleNewGame,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: '2048',
    board: (
      <div className="game-2048__board-area" ref={boardRef}>
        <Board2048
          board={gameState.board}
          tiles={gameState.tiles}
          boardSize={gameState.boardSize}
          disabled={gameState.gameStatus === 'game-over'}
        />
      </div>
    ),
    leftPanels: [
      {
        id: 'scores',
        title: 'Scores',
        icon: '📊',
        content: (
          <div className="game-2048__score-bar" role="status" aria-live="polite">
            <div className="game-2048__score-box">
              <span className="game-2048__score-label">Score</span>
              <span className="game-2048__score-value">{gameState.score}</span>
            </div>
            <div className="game-2048__score-box">
              <span className="game-2048__score-label">Best</span>
              <span className="game-2048__score-value">{gameState.bestScore}</span>
            </div>
            <div style={{ marginTop: '8px' }}>
              <span>Moves: {gameState.moveCount}</span>
            </div>
          </div>
        ),
      },
    ],
    statusBar: {
      turnIndicator: (
        <span>
          {view === 'game-over'
            ? 'Game Over'
            : view === 'won'
              ? '🎉 You reached 2048!'
              : `Score: ${gameState.score} | Moves: ${gameState.moveCount}`}
        </span>
      ),
      gameStatus: view === 'game-over' ? 'Game Over' : view === 'won' ? 'You Win!' : undefined,
      gameStatusType: view === 'game-over' || view === 'won' ? 'ended' : 'active',
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      { id: 'undo', label: 'Undo', icon: '↩', onClick: handleUndo, variant: 'secondary', disabled: gameState.moveHistory.length === 0 },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    gameOver: gameOverConfig,
  };

  return (
    <div className="game-2048">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/** Wrapped in ErrorBoundary */
const Game2048: React.FC<Game2048Props> = (props) => (
  <ErrorBoundary componentName="2048">
    <Game2048Inner {...props} />
  </ErrorBoundary>
);

export { Game2048 };
export default Game2048;
